import { ValidationPatterns } from '@constants';
import { ValidationType } from '@enums';
import i18n from '@translations';
import type { ValidationRuleOptions } from '@types';
import type {
  LengthValidationResult,
  NumberValidationResult,
  PatternValidationResult,
  ValidationContext,
  ValidationResult,
} from './types';

/**
 * Validates if a field is required and has a value
 */
export const validateRequired = (context: ValidationContext, rules: ValidationRuleOptions): ValidationResult => {
  const { value } = context;

  if (!value && rules.required) {
    return {
      isValid: false,
      message: i18n.t(rules.isSelection ? 'validation:selectRequired' : 'validation:required'),
    };
  }

  return { isValid: true };
};

/**
 * Validates string length constraints
 */
export const validateLength = (context: ValidationContext, rules: ValidationRuleOptions): LengthValidationResult => {
  const { value } = context;

  if (!value || (!rules.minLength && !rules.maxLength)) {
    return { isValid: true };
  }

  const length = value.length;
  const { minLength, maxLength } = rules;

  if (minLength && maxLength) {
    if (length < minLength || length > maxLength) {
      return {
        isValid: false,
        message: i18n.t('validation:formats.lengthRange', {
          min: minLength,
          max: maxLength,
        }),
        actualLength: length,
        expectedMin: minLength,
        expectedMax: maxLength,
      };
    }
  } else if (minLength && length < minLength) {
    return {
      isValid: false,
      message: i18n.t('validation:formats.minLength', { minLength }),
      actualLength: length,
      expectedMin: minLength,
    };
  } else if (maxLength && length > maxLength) {
    return {
      isValid: false,
      message: i18n.t('validation:formats.maxLength', { maxLength }),
      actualLength: length,
      expectedMax: maxLength,
    };
  }

  return { isValid: true };
};

/**
 * Validates numeric values and ranges
 */
export const validateNumericRange = (
  context: ValidationContext,
  rules: ValidationRuleOptions,
): NumberValidationResult => {
  const { value } = context;

  if (!value || !rules.pattern || rules.pattern !== ValidationType.NUMBERS) {
    return { isValid: true };
  }

  const numValue = Number(value);
  const patternRule = typeof rules.pattern === 'string' ? ValidationPatterns[rules.pattern] : rules.pattern;

  if (isNaN(numValue)) {
    return {
      isValid: false,
      message: i18n.t('validation:invalidFormat', {
        type: i18n.t(patternRule.type),
        description: i18n.t(patternRule.format),
      }),
    };
  }

  const { minLength: min, maxLength: max } = rules;

  if (min && max) {
    if (numValue < min || numValue > max) {
      return {
        isValid: false,
        message: i18n.t('validation:invalidFormat', {
          type: i18n.t(patternRule.type),
          description: i18n.t('validation:formats.numberRange', { min, max }),
        }),
        numericValue: numValue,
      };
    }
  } else if (min && numValue < min) {
    return {
      isValid: false,
      message: i18n.t('validation:invalidFormat', {
        type: i18n.t('validation:types.range'),
        description: i18n.t('validation:formats.minNumber', { min }),
      }),
      numericValue: numValue,
    };
  } else if (max && numValue > max) {
    return {
      isValid: false,
      message: i18n.t('validation:invalidFormat', {
        type: i18n.t('validation:types.range'),
        description: i18n.t('validation:formats.maxNumber', { max }),
      }),
      numericValue: numValue,
    };
  }

  return { isValid: true, numericValue: numValue };
};

/**
 * Validates pattern matching (regex)
 */
export const validatePattern = (context: ValidationContext, rules: ValidationRuleOptions): PatternValidationResult => {
  const { value } = context;

  if (!rules.pattern || !value) {
    return { isValid: true };
  }

  const patternRule = typeof rules.pattern === 'string' ? ValidationPatterns[rules.pattern] : rules.pattern;

  if (!patternRule.regex.test(value)) {
    return {
      isValid: false,
      message: i18n.t('validation:invalidFormat', {
        type: i18n.t(patternRule.type),
        description: i18n.t(patternRule.format),
      }),
      patternType: patternRule.type,
      patternFormat: patternRule.format,
    };
  }

  return { isValid: true };
};

/**
 * Validates custom rules
 */
export const validateCustomRule = (context: ValidationContext, rules: ValidationRuleOptions): ValidationResult => {
  const { value, formData } = context;

  if (!rules.customRule?.validator) {
    return { isValid: true };
  }

  const isValid = rules.customRule.validator({ value, formData });

  if (!isValid) {
    const message =
      typeof rules.customRule.message === 'function'
        ? rules.customRule.message({ value, formData })
        : rules.customRule.message;

    return {
      isValid: false,
      message: i18n.t(message),
    };
  }

  return { isValid: true };
};

/**
 * Checks if validation should be skipped based on conditions
 */
export const shouldSkipValidation = (context: ValidationContext, rules: ValidationRuleOptions): boolean => {
  const { value } = context;

  // Skip if no value and validation is not required for optional fields
  if (!value && !rules.validateWhenOptional) {
    return true;
  }

  return false;
};
